import {
  ColDef,
  ColGroupDef,
  GridApi,
  ICellRendererParams,
  IRowNode,
  RowClassParams,
  RowClassRules,
  RowHeightParams,
  RowStyle,
} from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import { Checkbox, Flex, Tooltip } from "@chakra-ui/react";
import clsx from "clsx";
import { FundFamilyUseAllocationTableRow } from "gen/model/fundFamilyUseAllocationTableRow";
import { cloneDeep, compact } from "lodash-es";
import React, { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { fundFamilyUseAllocationTableCalculateAmounts } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableCalculator";
import { FundFamilyUseAllocationTableColDefFactory } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableColDefFactory";
import { fundFamilyUseAllocationTableDefaultColDef } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableDefaultColDef";
import {
  useFundFamilyUseAllocationTableFormOnCellValueChanged,
  useFundFamilyUseAllocationTableGetErrorForCellFnFromForm,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableFormUtils";
import {
  FundFamilyUseAllocationTableContext,
  FundFamilyUseAllocationTableGetErrorForCellFn,
  FundFamilyUseAllocationTableMaybernTableCellErrorParams,
  FundFamilyUseAllocationTableProps,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableProps";
import { FundFamilyUseAllocationTableTheme } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableTheme";
import {
  FundFamilyUseAllocationTableCategoryRow,
  FundFamilyUseAllocationTableRowOrCategory,
  FundFamilyUseAllocationTableTotalRowData,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableTypes";
import {
  createFundFamilyUseAllocationTableEmptyRow,
  isFundFamilyAllocationTableCategoryRow,
  isFundFamilyAllocationTableRow,
  isFundFamilyUseAllocationTableTotalRowNode,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableUtils";
import { isFirstVisibleColumn } from "shared/components/table/ag/AgGridUtils";
import { MaybernTable } from "shared/components/table/maybern/MaybernTable";
import { MaybernTableGetErrorForCellFn } from "shared/components/table/maybern/MaybernTableErrors";
import { MaybernTableFocusCellRendererWrapper } from "shared/components/table/maybern/MaybernTableFocusCellRendererWrapper";
import { tryGetAgGridApi } from "shared/components/table/maybern/MaybernTableUtils";
import { useDetectRenderLoop } from "shared/utils/ReactUtils";
import { isTruthy } from "shared/utils/utils";
import { useDeepCompareEffect, useDeepCompareMemo } from "use-deep-compare";

export const FundFamilyUseAllocationTable: React.FC<
  FundFamilyUseAllocationTableProps
> = ({
  amountCellRendererSelector,
  columnCellRendererSelector,
  context: contextProp,
  getErrorForCell: getErrorForCellProp,
  autoAllocateTooltip,
  getAllocation,
  noRowsOverlayComponentParams,
  onBeforeRowChange,
  onRowAdd,
  onRowChange,
  onRowRemove,
  sx,
  tableData,
  tableDef,
  valueGetter,
}) => {
  useDetectRenderLoop();

  const { t } = useTranslation(["common"]);

  const ref =
    useRef<AgGridReact<FundFamilyUseAllocationTableRowOrCategory>>(null);

  const {
    headers,
    trailers,
    funds,
    enable_row_add_remove,
    enable_row_selection,
    show_total_row,
    categories,
    inputs_title,
  } = tableDef;
  const { rows } = tableData;
  const categoriesAllowAutoAllocate = categories.some(
    (category) => category.allow_auto_allocate,
  );

  const columnDefs = useMemo<(ColDef | ColGroupDef)[]>(() => {
    const columnDefFactory = new FundFamilyUseAllocationTableColDefFactory(
      t,
      tableDef,
      valueGetter,
    );

    const inputColumns = compact([
      !!categoriesAllowAutoAllocate && columnDefFactory.autoAllocateCol(),
      !!categoriesAllowAutoAllocate && columnDefFactory.fundFamily(),
      funds.map(columnDefFactory.fundCol),
    ]);
    return [
      enable_row_add_remove ? columnDefFactory.addRemoveCol() : undefined,
      headers?.map(columnDefFactory.headerCol),
      inputs_title
        ? {
            headerName: inputs_title,
            children: inputColumns.flat(),
            headerClass: "mb-use-allocation-header-inputs",
          }
        : inputColumns.flat(),
      trailers?.map(columnDefFactory.trailerCol),
      columnDefFactory.spacerCol(),
    ]
      .filter(isTruthy)
      .flat();
  }, [
    t,
    tableDef,
    valueGetter,
    categoriesAllowAutoAllocate,
    funds,
    enable_row_add_remove,
    headers,
    inputs_title,
    trailers,
  ]);

  const getErrorForCellFromForm =
    useFundFamilyUseAllocationTableGetErrorForCellFnFromForm();
  const onCellValueChangedForForm =
    useFundFamilyUseAllocationTableFormOnCellValueChanged();

  const getErrorForCell =
    useCallback<FundFamilyUseAllocationTableGetErrorForCellFn>(
      (params) =>
        [getErrorForCellFromForm(params), getErrorForCellProp?.(params)]
          .filter(isTruthy)
          .join(", "),
      [getErrorForCellFromForm, getErrorForCellProp],
    );

  const maybernTableGetErrorForCell = useCallback<
    MaybernTableGetErrorForCellFn<FundFamilyUseAllocationTableRowOrCategory>
  >(
    (params) => {
      if (!isFundFamilyAllocationTableRow(params.node.data)) {
        return;
      }
      return getErrorForCell(
        params as FundFamilyUseAllocationTableMaybernTableCellErrorParams,
      );
    },
    [getErrorForCell],
  );

  const context = useDeepCompareMemo<FundFamilyUseAllocationTableContext>(
    () => ({
      ...contextProp,
      amountCellRendererSelector,
      columnCellRendererSelector,
      getErrorForCell,
      autoAllocateTooltip,
      getAllocation,
      onBeforeRowChange,
      onRowAdd,
      onRowChange,
      onRowRemove,
      tableDef,
    }),
    [
      amountCellRendererSelector,
      columnCellRendererSelector,
      contextProp,
      getErrorForCell,
      autoAllocateTooltip,
      getAllocation,
      onBeforeRowChange,
      onRowAdd,
      onRowChange,
      onRowRemove,
      tableDef,
    ],
  );

  const recomputeData = useCallback(
    (api: GridApi<FundFamilyUseAllocationTableRow>) => {
      api.refreshCells({
        force: true,
      });
      api.resetRowHeights();
      api.forEachNode((node) => {
        if (isFundFamilyAllocationTableRow(node.data) && ref.current?.api) {
          fundFamilyUseAllocationTableCalculateAmounts({
            api,
            node,
            context,
          });
        }
      });
    },
    [context],
  );
  // recompute row data when context/getAllocation/errors changes
  useDeepCompareEffect(() => {
    void contextProp;
    void getAllocation;
    void getErrorForCell;
    const api = tryGetAgGridApi(ref.current);
    if (api) {
      recomputeData(api as GridApi<FundFamilyUseAllocationTableRow>);
    }
  }, [
    onBeforeRowChange,
    tableDef,
    contextProp,
    getAllocation,
    getErrorForCell,
  ]);

  const rowData = useMemo(() => {
    // cloneDeep because AG Grid likes to mutate rowData,
    // breaking dependency detection in hooks.
    const newRows: FundFamilyUseAllocationTableRowOrCategory[] = [
      ...cloneDeep(rows),
    ];
    if (categories.length > 1) {
      newRows.push(
        ...categories.map(
          (category) =>
            ({
              is_category: true,
              hideCategory: category.hide_category_header,
              id: category.id,
              category,
            }) as const,
        ),
      );
    }
    return newRows;
  }, [categories, rows]);

  const pinnedBottomRowData = useMemo(() => {
    const totalRow: FundFamilyUseAllocationTableTotalRowData = {
      isTotalRow: true,
    };
    return show_total_row ? [totalRow] : undefined;
  }, [show_total_row]);

  const getRowHeight: (
    params: RowHeightParams<FundFamilyUseAllocationTableRowOrCategory>,
  ) => number | undefined | null = useCallback((params) => {
    if (
      params.data &&
      (params.data as FundFamilyUseAllocationTableCategoryRow).hideCategory
    ) {
      return 0;
    }
  }, []);

  const getRowStyle: (
    params: RowClassParams<FundFamilyUseAllocationTableRowOrCategory>,
  ) => RowStyle | undefined = useCallback((params) => {
    if (
      params.data &&
      (params.data as FundFamilyUseAllocationTableCategoryRow).hideCategory
    ) {
      return {
        display: "none",
      };
    }
  }, []);

  const defaultColDef = useMemo<ColDef>(() => {
    if (categoriesAllowAutoAllocate) {
      return fundFamilyUseAllocationTableDefaultColDef;
    } else {
      return {
        ...fundFamilyUseAllocationTableDefaultColDef,
        colSpan: () => 1,
      };
    }
  }, [categoriesAllowAutoAllocate]);

  return (
    <MaybernTable<FundFamilyUseAllocationTableRowOrCategory>
      ref={ref}
      sx={{
        ...FundFamilyUseAllocationTableTheme,
        ...sx,
      }}
      className={clsx({
        "mb-use-allocation-has-categories": categories.length > 1,
      })}
      readOnlyEdit={true}
      animateRows={false}
      columnDefs={columnDefs}
      context={context}
      getErrorForCell={maybernTableGetErrorForCell}
      noRowsOverlayComponentParams={noRowsOverlayComponentParams}
      defaultColDef={defaultColDef}
      getRowId={(params) => params.data.id}
      rowClassRules={rowClassRules}
      rowData={rowData}
      pinnedBottomRowData={pinnedBottomRowData}
      rowSelection={enable_row_selection ? "multiple" : undefined}
      alwaysAggregateAtRootLevel={true}
      suppressRowClickSelection={true}
      suppressRowDeselection={true}
      tooltipTrigger="hover"
      tooltipShowDelay={0}
      tooltipInteraction={true}
      tooltipMouseTrack={true}
      popupParent={document.body}
      isRowSelectable={(rowNode) =>
        Boolean(
          enable_row_selection &&
            !isFundFamilyUseAllocationTableTotalRowNode(rowNode),
        )
      }
      onFirstDataRendered={(event) => {
        const nodesToSelect: IRowNode<FundFamilyUseAllocationTableRowOrCategory>[] =
          [];
        event.api.forEachNode((node) => {
          if (
            isFundFamilyAllocationTableRow(node.data) &&
            node.data.is_selected
          ) {
            nodesToSelect.push(node);
          }
        });
        event.api.setNodesSelected({ nodes: nodesToSelect, newValue: true });
        recomputeData(event.api as GridApi<FundFamilyUseAllocationTableRow>);
      }}
      onRowSelected={(event) => {
        if (isFundFamilyAllocationTableRow(event.node.data)) {
          const newData: FundFamilyUseAllocationTableRow = {
            ...event.node.data,
            is_selected: event.node.isSelected(),
          };
          event.node.setData(newData);
          onRowChange({ row: newData });
        }
      }}
      onCellKeyDown={(event) => {
        const keyboardEvent = event.event as KeyboardEvent | null | undefined;
        if (
          "column" in event &&
          isFirstVisibleColumn(event.api, event.column) &&
          keyboardEvent?.key === " " &&
          event.node.selectable &&
          isFundFamilyAllocationTableRow(event.node.data) &&
          !event.node.data.is_read_only
        ) {
          event.node.setSelected(!event.node.isSelected());
        }
      }}
      onCellValueChanged={(params) => {
        if (!isFundFamilyAllocationTableRow(params.node.data)) {
          return;
        }
        // there are some cases where we want to recalculate allocations even when manually changing cell values (not user input) so we have to call recomputeData
        recomputeData(params.api as GridApi<FundFamilyUseAllocationTableRow>);
        onCellValueChangedForForm(params);
        onRowChange({ row: params.node.data });
      }}
      onSortChanged={(event) => {
        event.api.refreshCells({
          columns: ["addRemove"],
        });
      }}
      treeData={categories.length > 1}
      treeDataDisplayType="custom"
      groupDefaultExpanded={1}
      fullWidthCellRenderer={CategoryFullWidthCellRenderer}
      isFullWidthRow={(params) => {
        const {
          tableDef: { categories },
        }: FundFamilyUseAllocationTableContext = params.context;
        return (
          categories.length > 1 &&
          isFundFamilyAllocationTableCategoryRow(params.rowNode.data)
        );
      }}
      getDataPath={(data) =>
        categories.length > 1 && isFundFamilyAllocationTableRow(data)
          ? [data.category_id, data.id]
          : [data.id]
      }
      getRowHeight={getRowHeight}
      getRowStyle={getRowStyle}
    />
  );
};

const CategoryFullWidthCellRenderer: React.FC<ICellRendererParams> = (
  params,
) => {
  const {
    tableDef,
    onRowAdd,
    onRowRemove,
  }: FundFamilyUseAllocationTableContext = params.context;

  const categoryId = params.value;
  const category = tableDef.categories.find(
    (category) => category.id === categoryId,
  )!;

  const childRows = params.node.childrenAfterGroup;

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;
      if (isChecked) {
        onRowAdd?.(
          createFundFamilyUseAllocationTableEmptyRow(tableDef, category),
        );
      } else {
        childRows?.forEach((rowNode) => onRowRemove?.(rowNode));
      }
    },
    [category, childRows, onRowAdd, onRowRemove, tableDef],
  );

  const ref = useRef<HTMLInputElement>(null);

  const checkbox = category.hide_checkbox ? (
    category.title
  ) : (
    <Checkbox
      isChecked={!!params.node.allChildrenCount}
      onChange={onChange}
      ref={ref}
      isDisabled={category.is_disabled}
    >
      {category.title}
    </Checkbox>
  );

  return (
    <MaybernTableFocusCellRendererWrapper params={params} inputRef={ref}>
      <Flex alignItems="center" h="100%" px="24px" bg="primary.25">
        {category.is_disabled ? (
          <Tooltip label={category.disabled_reason} shouldWrapChildren={true}>
            {checkbox}
          </Tooltip>
        ) : (
          checkbox
        )}
      </Flex>
    </MaybernTableFocusCellRendererWrapper>
  );
};

const rowClassRules: RowClassRules = {
  "mb-row-not-selected": (params) =>
    params.node.selectable && !params.node.isSelected(),
  "mb-row-is-total": (params) =>
    isFundFamilyUseAllocationTableTotalRowNode(params.node),
};
